/**
 * One weighted option in a sampling pool — how likely this value is to be
 * drawn for a given field, relative to its siblings. Weights need not sum to
 * any particular total; the sampler normalises by the pool's own sum.
 *
 * An interface, not a class like this package's other entities: this file is
 * also exported as raw TypeScript source (`./lookalike-schema`, see this
 * package's own `package.json`), so apps/web's own `tsc` compiles it under
 * `strict` with no Nest-style relaxation — a class field with no initialiser
 * fails `strictPropertyInitialization` there even though nothing here is ever
 * `new`'d.
 */
export interface WeightedValue<T = string> {
  value: T;
  weight: number;
}

export type LookalikeSavviness = 'low' | 'medium' | 'high';

/**
 * A continuous field's range, sampled uniformly (then jittered) rather than
 * from a discrete pool — age and years of experience have too many plausible
 * values to enumerate, and a pool of ages reads as arbitrary bucketing where a
 * range reads as what it is.
 */
export interface LookalikeRange {
  min: number;
  max: number;
}

/**
 * What one seed persona's lookalike population is drawn from.
 *
 * Written once by the model (see `persona-lookalikes.service.ts`'s schema
 * call) from the seed's own fields, then sampled entirely client- and
 * server-side with no further model calls — generating 1000 lookalikes is a
 * seeded-PRNG pass over this schema, not 1000 LLM calls. Every pool's weights
 * are what the "adjust weights" sliders edit; adjusting one re-samples the
 * population instantly.
 *
 * Deliberately a narrower slice of a full `Persona`: the fields worth varying
 * and worth plotting (age, experience, where they work, what job, what they
 * want, what stops them, how they decide) — not the full twenty-field profile
 * a hand-written or generated persona carries. A lookalike is a point in a
 * population, not a person with a name, until it is promoted into one.
 */
export interface LookalikeSchema {
  ageRange: LookalikeRange;
  experienceYearsRange: LookalikeRange;
  locations: WeightedValue[];
  jobTitles: WeightedValue[];
  companySizes: WeightedValue[];
  goals: WeightedValue[];
  painPoints: WeightedValue[];
  techSavviness: WeightedValue<LookalikeSavviness>[];
  brandLoyalty: WeightedValue<LookalikeSavviness>[];
}

/** The pools `UpdateLookalikeWeightsDto` can adjust — every field on
 *  `LookalikeSchema` that is a weighted pool rather than a continuous range. */
export const LOOKALIKE_WEIGHT_FIELDS = [
  'locations',
  'jobTitles',
  'companySizes',
  'goals',
  'painPoints',
  'techSavviness',
  'brandLoyalty',
] as const;

export type LookalikeWeightField = (typeof LOOKALIKE_WEIGHT_FIELDS)[number];
